import clsx from 'clsx'
import { ReactNode } from 'react'

import { Widont } from '@/components/Widont'
import { formatDate } from '@/utils/formatDate'
import { Prose } from '@/components/Prose'
import { AuthorsMini } from '@/components/marketing/Authors'
import { BlogMeta } from '@/types/blog'

interface ContainerProps {
  className?: string
  children: ReactNode
}

interface DividerProps {
  classes?: string
}

interface ArticleContainerProps {
  blogMeta: BlogMeta
  proseChildren: ReactNode
  includeDate?: boolean
}

export function DashboardItemsFlexContainer({
  className,
  children,
}: ContainerProps) {
  return (
    <div className={clsx('flex shrink-0 items-stretch gap-3', className)}>
      {children}
    </div>
  )
}

export function ProductBannerContainer({ children }: { children: ReactNode }) {
  return (
    <div className="max-w-8xl mx-auto mb-24 mt-12 px-4 lg:px-8 xl:px-12">
      {children}
    </div>
  )
}

export function FeaturePageContainer({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto px-4 pb-8 sm:px-6 md:px-8 lg:max-w-7xl">
      {children}
    </div>
  )
}

export function Divider({ classes }: DividerProps) {
  return <dl className={clsx('border-t border-slate-800', classes)} />
}

export function PostContainer({ className, children }: ContainerProps) {
  return (
    <div
      className={clsx(
        'relative mx-auto flex justify-center pb-8 sm:px-2 lg:px-8',
        className
      )}
    >
      {children}
    </div>
  )
}

export function ArticleContainer({
  blogMeta,
  proseChildren,
  includeDate = true,
}: ArticleContainerProps) {
  return (
    <article className="relative pt-10">
      <header className="mb-9">
        <h1
          className={clsx(
            'text-2xl font-extrabold tracking-tight text-slate-200 md:text-3xl '
          )}
        >
          <Widont>{blogMeta.title}</Widont>
        </h1>
        {includeDate && blogMeta.date && (
          <div className="text-sm leading-6">
            <dl>
              <dt className="sr-only">Date</dt>
              <dd className={clsx('absolute inset-x-0 top-0 text-slate-400')}>
                <time dateTime={blogMeta.date} suppressHydrationWarning={true}>
                  {formatDate(blogMeta.date, '{dddd}, {MMMM} {DD}, {YYYY}')}
                </time>
              </dd>
            </dl>
          </div>
        )}
        {blogMeta.authors.length > 0 && (
          <div className="mt-6">
            <AuthorsMini authors={blogMeta.authors} />
          </div>
        )}
      </header>
      <Prose>{proseChildren}</Prose>
    </article>
  )
}
